import React, { CSSProperties, useMemo, useRef, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useQuery } from 'components/hooks/useQuery';
import { usePartialPublishEventDetails } from 'screens/performanceReview/helpers';
import { addPluralSuffixS } from 'utils/helpers/stringHelpers';

import {
  sendPPConfirmationCancelEvent,
  sendPPConfirmationCheckboxEvent,
} from 'constants/mixpanelHelpers/partialPublishEvents';
import { sendPublishEvent } from 'constants/mixpanelHelpers/reviewsEvents';

import { AgGridWrapper } from 'uiToolkitV1/AgGridWrapper';
import { Avatar } from 'uiToolkitV1/Avatar';
import { DrawerBody, DrawerFooter, DrawerHeaderSecondary } from 'uiToolkitV1/BottomDrawer';
import { Button } from 'uiToolkitV1/Button';
import { Checkbox } from 'uiToolkitV1/Checkbox';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { TagColored } from 'uiToolkitV1/Tag';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { SearchBar } from '../SearchBar';
import { REVIEW_STATUS_COLORS, REVIEW_STATUS_LABELS, usePartialPublishContext } from './PartialPublishContext';
import { EmployeeWithReviewStatus, useSubmitPartialPublishMutation } from './dataHooks';

const AvatarComponent = (data: ICellRendererParams<EmployeeWithReviewStatus>['data']) => () => (
  <Avatar name={data.employeeDetails.displayName} src={data.employeeDetails.profilePhoto} />
);

const EmployeeRenderer: React.FC<ICellRendererParams<EmployeeWithReviewStatus>> = ({ data }) => (
  <ListItemComp
    LeftComponent={AvatarComponent(data)}
    title={data.employeeDetails.displayName}
    withoutBackground
    isBoldText={false}
  />
);

const StatusRenderer: React.FC<ICellRendererParams<EmployeeWithReviewStatus>> = ({ data }) => (
  <TagColored color={REVIEW_STATUS_COLORS[data.reviewStatus]}>{REVIEW_STATUS_LABELS[data.reviewStatus]}</TagColored>
);

const EmployeesList = ({ onClose }) => {
  const { selectedSection, setScreen } = usePartialPublishContext();

  const { mutate: submitPartialPublish } = useSubmitPartialPublishMutation();

  const [isAgree, setIsAgree] = useState(false);

  const { reviewCycleId } = useQuery();

  const columnDefs = useMemo<ColDef<EmployeeWithReviewStatus>[]>(
    () => [
      {
        colId: 'emp-name',
        flex: 1,
        cellRenderer: EmployeeRenderer,
        valueGetter: ({ data }) => data.employeeDetails.displayName,
      },
      {
        colId: 'reviewStatus',
        cellRenderer: StatusRenderer,
        valueGetter: ({ data }) => data.reviewStatus,
      },
    ],
    []
  );

  const gridStyle = useMemo<CSSProperties>(
    () => ({
      height: 'calc(88vh - 230px)',
      width: '100%',
      marginTop: '8px',
    }),
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      cellStyle: { display: 'flex', alignItems: 'center', cursor: 'default', padding: '0 8px' },
    }),
    []
  );

  const gridRef = useRef<AgGridReact>();

  const gridStatePersistenceKey = null;

  const { empId, page } = usePartialPublishEventDetails();

  const onChangeWithEvent = () => {
    sendPPConfirmationCheckboxEvent({
      eventProperties: {
        page,
        source: 'Partial Publish',
        empId,
      },
    });
    setIsAgree((prev) => !prev);
  };

  const onCloseWithEvent = () => {
    sendPPConfirmationCancelEvent({
      eventProperties: {
        page,
        source: 'Partial Publish',
        empId,
        publishType:
          selectedSection.section === 'ALLOW_MANAGER_PUBLISH' ? 'Publish to manager' : 'Publish to employees',
      },
    });
    onClose();
  };

  const onSubmitWithEvent = () => {
    sendPublishEvent({
      eventProperties: {
        page,
        source: 'Partial Publish',
        empId,
        reviewCycleId,
        publishType: selectedSection.section === 'ALLOW_MANAGER_PUBLISH' ? 'Publish to manager' : 'Publish to employee',
      },
    });
    submitPartialPublish();
    setScreen('RESULT');
  };

  if (!selectedSection) {
    return null;
  }

  return (
    <>
      <DrawerHeaderSecondary onClose={onClose}>
        <Flex
          alignItems="center"
          borderBottom="1px"
          borderBottomColor="neutral.CFD7DF"
          mx={4}
          py={5}
          justifyContent="space-between"
        >
          <TextBodyMedium bold>
            {selectedSection.section === 'ALLOW_MANAGER_PUBLISH'
              ? 'Enable managers to share results for'
              : 'Share review cycle results directly with'}{' '}
            {selectedSection.employeeStatusList.length} Employee
            {addPluralSuffixS(selectedSection.employeeStatusList.length)}?
          </TextBodyMedium>
        </Flex>
      </DrawerHeaderSecondary>
      <DrawerBody>
        <Box mx={4} py={4}>
          <SearchBar gridRef={gridRef} placeholder="Search Employees" width="100%" />
          <div style={gridStyle} className="ag-theme-alpine ag-theme-partial-publish">
            <AgGridWrapper
              gridStatePersistenceKey={gridStatePersistenceKey}
              useColumnStatePersistence={false}
              useFilterModelPersistence={false}
              ref={gridRef}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowData={selectedSection.employeeStatusList}
              headerHeight={0}
              groupHeaderHeight={0}
              rowHeight={56}
            />
          </div>
        </Box>
      </DrawerBody>
      <DrawerFooter>
        <Checkbox ml={1} mr="auto" isChecked={isAgree} onChange={onChangeWithEvent}>
          {selectedSection.section === 'ALLOW_MANAGER_PUBLISH'
            ? 'I agree to enable managers to share results'
            : 'I agree to share results to employees'}
        </Checkbox>
        <Button w="94px" variant="outlined" mr={4} onClick={onCloseWithEvent}>
          Cancel
        </Button>
        <Button variant="filled" colorScheme="blue" onClick={onSubmitWithEvent} disabled={!isAgree}>
          Share results
        </Button>
      </DrawerFooter>
    </>
  );
};

export default EmployeesList;
